<template>
  <div class="image-upload" :class="{ 'image-upload--readonly': readonly }">
    <b-upload
      v-model="imageFile"
      class="image-upload-input"
      :style="{ height: `${height}px` }"
      :class="customClass"
      drag-drop
      accept="image/*"
      :disabled="disabled || readonly"
    >
      <section class="image-upload-section">
        <div
          class="image-upload-img"
          :class="`image-upload-img--${sizing}`"
          @click="conditionallyStop"
        >
          <img
            v-if="imagePreviewUrl"
            ref="image"
            :src="imagePreviewUrl"
            @crop="handleCrop"
          />
          <b-icon v-else :icon="emptyIcon" size="is-large"></b-icon>
        </div>
        <div class="image-upload-text">{{ imageUploadText }}</div>
      </section>
    </b-upload>
  </div>
</template>

<script>
import "cropperjs/dist/cropper.css";
import Vue from "vue";
import Cropper from "cropperjs";
import { TABLET } from "@/constants/breakpoints";

export const Sizing = {
  FULL: "full",
  FORCE_CIRCLE: "force-circle",
  CROP_SQUARE: "crop-square"
};

export default {
  props: {
    value: {
      type: File,
      default: null
    },
    previewUrl: {
      type: String,
      default: null
    },
    emptyIcon: {
      type: String,
      default: "image-plus"
    },
    cropping: {
      type: Object,
      default: null
    },
    sizing: {
      type: String,
      default: Sizing.FULL,
      validator: function(value) {
        for (const key in Sizing) {
          if (Sizing[key] == value) {
            return true;
          }
        }
        return false;
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    customClass: {
      type: String,
      default: "",
      required: false
    }
  },
  data() {
    return {
      imageFileUrl: null,
      cropper: null,
      height: 0
    };
  },
  computed: {
    imageFile: {
      get() {
        return this.image;
      },
      set(image) {
        this.$emit("input", image);
      }
    },
    imagePreviewUrl() {
      return this.imageFileUrl || this.previewUrl;
    },
    imageUploadText() {
      if (window.innerWidth < TABLET) {
        return this.imagePreviewUrl
          ? "Tap to change the image"
          : "Tap to select an image";
      }
      const action = this.imagePreviewUrl ? "update" : "upload";
      return `Drag & drop an image or click here to ${action}`;
    }
  },
  watch: {
    value(value) {
      if (value) {
        // Read in the selected image file as a data URL.
        const reader = new FileReader();
        reader.onload = e => {
          this.imageFileUrl = e.target.result;
          if (this.sizing == Sizing.CROP_SQUARE) {
            Vue.nextTick(() => {
              // Wait for the DOM to update.
              this.updateCropper();
            });
          }
        };
        reader.readAsDataURL(value);
      } else {
        this.imageFileUrl = null;
      }
    }
  },
  mounted() {
    if (this.sizing == Sizing.CROP_SQUARE && this.imagePreviewUrl) {
      this.setupCropper();
    }
    window.addEventListener("resize", this.resize);
    this.resize();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resize);
  },
  methods: {
    handleCrop(event) {
      this.$emit("update:cropping", {
        x: Math.round(event.detail.x),
        y: Math.round(event.detail.y),
        height: Math.round(event.detail.height),
        width: Math.round(event.detail.width)
      });
    },
    conditionallyStop(event) {
      if (this.cropper) {
        event.stopPropagation();
        event.preventDefault();
      }
    },
    setupCropper() {
      const imageElement = this.$refs.image;
      this.cropper = new Cropper(imageElement, {
        aspectRatio: 1,
        autoCropArea: 1,
        checkCrossOrigin: false,
        initialAspectRatio: 1,
        scalable: false,
        viewMode: 2,
        zoomable: false,
        data: this.cropping
      });
    },
    updateCropper() {
      if (this.cropper) {
        this.cropper.replace(this.imageFileUrl);
      } else {
        this.setupCropper();
      }
    },
    resize() {
      // Explicitly set the height of the upload input based on the height of
      // the container. Setting 'height: 100%' will not expand the input to
      // fill the space properly because of the block positioning of the image.
      //
      // First shrink the upload input so the container has a chance to shrink,
      // then resize to the height of the container.
      this.height = 0;
      this.$nextTick(() => {
        this.height = Math.max(0, this.$el.clientHeight);
      });
    }
  }
};
</script>

<style lang="scss">
@import "@/scss/colors.scss";

$text-height: 32px;

.image-upload {
  flex: 1 1 auto;
  width: 100%;

  &-input {
    min-height: 194px;
    width: 100%;
  }

  .upload-draggable {
    height: 100%;
    overflow: hidden;
    padding: 0;
    width: 100%;
  }

  &-section {
    height: 100%;
    width: 100%;
  }

  &-img {
    align-items: center;
    background: #fff;
    display: flex;
    height: calc(100% - #{$text-height});
    justify-content: center;
    padding: 10px;
    width: 100%;

    img {
      display: block;
      max-height: 100%;
      max-width: 100%;
      min-height: 0;
    }

    &--force-circle {
      img {
        border: 1px solid $light-gray;
        border-radius: 50%;
        height: 122px;
        overflow: hidden;
        width: 122px;
      }
    }

    &--crop-square {
      padding: 0;
    }

    .icon {
      opacity: 0.2;
    }
  }

  &-text {
    background: rgba($primary, 0.05);
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    flex: 0;
    font-size: 14px;
    height: $text-height;
    line-height: 24px;
    padding: 5px 10px;
    text-align: center;
  }

  &--readonly {
    .upload-draggable.is-disabled,
    .upload-draggable.is-disabled:hover {
      background: #fff;
      border: 1px solid rgb(219, 219, 219);
      border-radius: 2px;
      opacity: 1;
    }
    .image-upload {
      &-img {
        height: 100%;
      }
      &-text {
        display: none;
      }
    }
  }
}
</style>
